import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { getAuthHeaders } from '../utils/odotId';
import { primeYouTubeCache } from '../utils/youtubeCache';
import './SearchPage.css';

interface SearchHistoryEntry {
  reportId: string;
  queryDisplay: string;
  title: string;
  searchedAt: string;
  genreTags?: string[];
}

interface ConversationTurn {
  role: 'user' | 'agent';
  content: string;
}

export function SearchPage() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'spinning' | 'zoom-out' | 'zoom-in' | 'bouncing'>('idle');
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [communitySearches, setCommunitySearches] = useState<SearchHistoryEntry[]>([]);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const animationCompleteRef = useRef<(() => void) | null>(null);

  // Animation sequence: spin 2s → zoom-out 0.6s → zoom-in 0.6s → bounce 3s
  // Total: 6.2 seconds before allowing navigation
  useEffect(() => {
    if (isLoading) {
      setAnimationPhase('spinning');
      const timers: NodeJS.Timeout[] = [];

      // After 2s spinning, start zoom-out transition
      timers.push(setTimeout(() => {
        setAnimationPhase('zoom-out');
      }, 2000));

      // After zoom-out (0.6s), switch to lips and zoom-in
      timers.push(setTimeout(() => {
        setAnimationPhase('zoom-in');
      }, 2600));

      // After zoom-in (0.6s), start bouncing
      timers.push(setTimeout(() => {
        setAnimationPhase('bouncing');
      }, 3200));

      // After 3s of bouncing, animation is complete
      timers.push(setTimeout(() => {
        if (animationCompleteRef.current) {
          animationCompleteRef.current();
          animationCompleteRef.current = null;
        }
      }, 6200));

      return () => timers.forEach(t => clearTimeout(t));
    } else {
      setAnimationPhase('idle');
    }
  }, [isLoading]);

  // Returns a promise that resolves when animation is complete
  const waitForAnimation = (): Promise<void> => {
    return new Promise((resolve) => {
      animationCompleteRef.current = resolve;
    });
  };

  // Theme state
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tastymusic-theme');
      if (saved === 'dark' || saved === 'light') return saved;
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('tastymusic-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Fetch community searches on mount
  useEffect(() => {
    fetchCommunitySearches();
  }, []);

  // Focus input when conversation updates
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [conversation, isLoading]);

  const fetchCommunitySearches = async () => {
    try {
      const response = await fetch('/api/v1/research/community', {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCommunitySearches(data.searches || []);
      }
    } catch (error) {
      console.error('Failed to fetch community searches:', error);
    }
  };

  const viewCachedReport = async (reportId: string) => {
    setLoadingReportId(reportId);
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/research/report/${reportId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.youtubeCache) {
          primeYouTubeCache(data.youtubeCache);
        }
        // Navigate to report page
        navigate(`/report/${reportId}`);
      } else {
        console.error('Report not found');
      }
    } catch (error) {
      console.error('Failed to fetch cached report:', error);
    } finally {
      setIsLoading(false);
      setLoadingReportId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userInput = input.trim();
    setIsLoading(true);

    try {
      // Start both the API call and animation timer
      const animationPromise = waitForAnimation();
      const apiPromise = fetch('/api/v1/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          input: userInput,
          history: conversation,
        }),
      }).then(res => res.json());

      // Wait for both to complete
      const [data] = await Promise.all([apiPromise, animationPromise]);

      if (data.status === 'ready') {
        // Navigate to research page with query data
        navigate('/research', {
          state: {
            query: data.result,
            locationResolved: data.locationResolved,
          }
        });
      } else if (data.status === 'question') {
        setConversation(data.conversation);
        setInput('');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Query failed:', error);
      setConversation(prev => [
        ...prev,
        { role: 'user', content: userInput },
        { role: 'agent', content: "Something went wrong. Try again?" }
      ]);
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setConversation([]);
    setInput('');
    setIsLoading(false);
    setLoadingReportId(null);
    setSelectedGenres([]);
    fetchCommunitySearches();
  };

  const isInConversation = conversation.length > 0;

  // Filter by selected genres (AND logic - must have ALL selected genres)
  const filteredCommunity = selectedGenres.length > 0
    ? communitySearches.filter(e => selectedGenres.every(genre => e.genreTags?.includes(genre)))
    : communitySearches;

  const addGenreFilter = (genre: string) => {
    if (!selectedGenres.includes(genre)) {
      setSelectedGenres(prev => [...prev, genre]);
    }
  };

  const removeGenre = (genre: string) => {
    setSelectedGenres(prev => prev.filter(g => g !== genre));
  };

  return (
    <div className="search-page" data-theme={theme}>
      <div className="controls-panel">
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>
      <main className="container">
        <header className={`hero ${isInConversation ? 'compact' : ''}`}>
          <div className="logo-wrapper">
            <img
              src={animationPhase === 'zoom-in' || animationPhase === 'bouncing' || isInConversation ? '/logo.png' : '/logo-resting.png'}
              alt=""
              className={`logo-image ${animationPhase} ${isInConversation && animationPhase === 'idle' ? 'conversation' : ''}`}
              onClick={handleReset}
            />
          </div>
          {!isInConversation && animationPhase !== 'zoom-in' && animationPhase !== 'bouncing' && (
            <p className="tagline chicle">
              DISCOVER MUSIC
            </p>
          )}
        </header>

        {isInConversation && (
          <div className="conversation">
            {conversation.map((turn, i) => (
              <div key={i} className={`message ${turn.role}`}>
                {turn.content}
                {turn.role === 'agent' && i === conversation.length - 1 && (
                  <span className="or-something-else"> Or something else?</span>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message agent thinking-dots">
                <span>.</span><span>.</span><span>.</span>
              </div>
            )}
          </div>
        )}

        <form className="search-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder={isInConversation ? "Type your answer..." : "shoegaze but darker..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isLoading}
            autoFocus
          />
          <button
            type="submit"
            className="search-button"
            disabled={isLoading || !input.trim()}
          >
            {isLoading ? '...' : isInConversation ? 'Reply' : 'Go'}
          </button>
        </form>

        {!isInConversation && (
          <>
            <footer className="hint">
              <p>Try anything: "dreamy but heavy" · "Brooklyn post-punk" · "bitch energy"</p>
            </footer>

            {communitySearches.length > 0 && (
              <section className="recent-searches-panel">
                <h2 className="explore-heading">EXPLORE</h2>
                {selectedGenres.length > 0 && (
                  <div className="active-filters-row">
                    {selectedGenres.map(genre => (
                      <button
                        key={genre}
                        className="clear-filter-btn"
                        onClick={() => removeGenre(genre)}
                        title={`Remove ${genre} filter`}
                      >
                        {genre} ✕
                      </button>
                    ))}
                  </div>
                )}

                <div className="search-column full-width">
                  <div className="history-list community-grid">
                    {filteredCommunity
                      .filter(entry => entry.title && entry.title !== 'Discovering artists...')
                      .slice(0, 50)
                      .map((entry) => {
                      const isThisLoading = loadingReportId === entry.reportId;
                      return (
                        <button
                          key={entry.reportId}
                          className={`history-item ${isThisLoading ? 'loading' : ''}`}
                          onClick={() => viewCachedReport(entry.reportId)}
                          disabled={isLoading}
                        >
                          <div className="history-content">
                            <span className="history-title">{entry.title}</span>
                            <span className="history-query">{entry.queryDisplay}</span>
                            {entry.genreTags && entry.genreTags.length > 0 && (
                              <span className="history-genres">
                                {entry.genreTags.slice(0, 3).map(tag => (
                                  <button
                                    key={tag}
                                    className={`genre-tag-chip ${selectedGenres.includes(tag) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addGenreFilter(tag);
                                    }}
                                  >
                                    {tag}
                                  </button>
                                ))}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
